// PDFs simples gerados no próprio aparelho (recibo, declaração, relatório), sem servidor e sem módulo nativo.
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { salvarECompartilhar } from './arquivoSalvar';

export type BlocoPdf =
  | { t: 'titulo'; texto: string }
  | { t: 'sub'; texto: string }
  | { t: 'par'; texto: string; negrito?: boolean; centro?: boolean; tam?: number }
  | { t: 'espaco'; h?: number }
  | { t: 'linha' }
  | { t: 'assinatura'; linhas: string[] }
  | { t: 'tabela'; colunas: string[]; linhas: string[][]; larguras?: number[] }
  /** texto 28 (C2): foto (JPEG ou PNG), do tamanho da largura da página, com a legenda embaixo */
  | { t: 'imagem'; bytes: Uint8Array; legenda?: string; altMax?: number };

import { limparTexto } from './pdfTexto';
export { limparTexto };

function quebrar(texto: string, fonte: PDFFont, tam: number, largura: number) {
  const out: string[] = [];
  for (const par of limparTexto(texto).split('\n')) {
    let linha = '';
    for (const p of par.split(/\s+/)) {
      const teste = linha ? linha + ' ' + p : p;
      if (fonte.widthOfTextAtSize(teste, tam) > largura && linha) { out.push(linha); linha = p; } else linha = teste;
    }
    out.push(linha);
  }
  return out;
}

/** Monta o PDF (A4) a partir dos blocos e devolve os bytes. */
export async function montarPdf(blocos: BlocoPdf[], rodape?: string): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const normal = await doc.embedFont(StandardFonts.Helvetica);
  const negrito = await doc.embedFont(StandardFonts.HelveticaBold);
  const W = 595.28, H = 841.89, M = 56, larg = W - 2 * M;
  let pg: PDFPage = doc.addPage([W, H]);
  let y = H - M;
  const nova = () => { pg = doc.addPage([W, H]); y = H - M; };
  const precisa = (h: number) => { if (y - h < M + 20) nova(); };
  const escrever = (txt: string, f: PDFFont, tam: number, centro = false, x0 = M, l = larg) => {
    for (const linha of quebrar(txt, f, tam, l)) {
      precisa(tam * 1.5);
      const x = centro ? x0 + (l - f.widthOfTextAtSize(linha, tam)) / 2 : x0;
      pg.drawText(linha, { x, y: y - tam, size: tam, font: f, color: rgb(0.1, 0.1, 0.12) });
      y -= tam * 1.45;
    }
  };
  for (const b of blocos) {
    if (b.t === 'titulo') { escrever(b.texto, negrito, 16, true); y -= 8; }
    else if (b.t === 'sub') { escrever(b.texto, normal, 9.5, true); y -= 4; }
    else if (b.t === 'par') { escrever(b.texto, b.negrito ? negrito : normal, b.tam ?? 11, !!b.centro); y -= 6; }
    else if (b.t === 'espaco') y -= b.h ?? 14;
    else if (b.t === 'linha') { precisa(10); pg.drawLine({ start: { x: M, y: y - 4 }, end: { x: W - M, y: y - 4 }, thickness: 0.6, color: rgb(0.6, 0.6, 0.6) }); y -= 12; }
    else if (b.t === 'assinatura') {
      precisa(30 + b.linhas.length * 14);
      y -= 34;
      pg.drawLine({ start: { x: W / 2 - 130, y }, end: { x: W / 2 + 130, y }, thickness: 0.7, color: rgb(0.2, 0.2, 0.2) });
      y -= 6;
      b.linhas.forEach((l, i) => escrever(l, i === 0 ? negrito : normal, i === 0 ? 11 : 9.5, true));
    } else if (b.t === 'imagem') {
      try {
        const png = b.bytes[0] === 0x89 && b.bytes[1] === 0x50;
        const img = png ? await doc.embedPng(b.bytes) : await doc.embedJpg(b.bytes);
        const altMax = b.altMax ?? 300;
        const k = Math.min(larg / img.width, altMax / img.height, 1.5);
        const w = img.width * k, h = img.height * k;
        precisa(h + (b.legenda ? 16 : 6));
        pg.drawImage(img, { x: M + (larg - w) / 2, y: y - h, width: w, height: h });
        y -= h + 4;
        if (b.legenda) escrever(b.legenda, normal, 9, true);
        y -= 8;
      } catch { /* foto que não abriu: segue sem ela */ }
    } else if (b.t === 'tabela') {
      const n = b.colunas.length;
      const lw = b.larguras ?? b.colunas.map(() => 1 / n);
      const xs = lw.reduce<number[]>((acc, w, i) => [...acc, (acc[i] ?? M) + w * larg], [M]);
      const linha = (cel: string[], f: PDFFont) => {
        const alturas = cel.map((c, i) => quebrar(c, f, 9, lw[i] * larg - 6).length);
        const h = Math.max(...alturas) * 12 + 4;
        precisa(h);
        cel.forEach((c, i) => { let yy = y - 9; for (const l of quebrar(c, f, 9, lw[i] * larg - 6)) { pg.drawText(l, { x: xs[i] + 2, y: yy, size: 9, font: f }); yy -= 12; } });
        y -= h;
        pg.drawLine({ start: { x: M, y: y + 2 }, end: { x: W - M, y: y + 2 }, thickness: 0.3, color: rgb(0.75, 0.75, 0.75) });
      };
      linha(b.colunas, negrito);
      b.linhas.forEach(l => linha(l, normal));
      y -= 8;
    }
  }
  if (rodape) doc.getPages().forEach((p, i, todas) => p.drawText(limparTexto(`${rodape} · ${i + 1}/${todas.length}`), { x: M, y: 30, size: 8, font: normal, color: rgb(0.5, 0.5, 0.5) }));
  return doc.save();
}

/** Nome de arquivo seguro em qualquer aparelho: sem acentos nem caracteres proibidos. */
export const nomeArquivo = (n: string) => n.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\\/:*?"<>|·•]+/g, '-').replace(/\s+/g, ' ').trim().slice(0, 120) || 'arquivo';

export async function gerarECompartilharPdf(nome: string, blocos: BlocoPdf[], rodape?: string) {
  const bytes = await montarPdf(blocos, rodape);
  await salvarECompartilhar(bytes, nomeArquivo(nome.endsWith('.pdf') ? nome : nome + '.pdf'), 'application/pdf');
}

export async function compartilharCsv(nome: string, linhas: (string | number)[][]) {
  const esc = (v: string | number) => { const s = String(v ?? ''); return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const txt = '﻿' + linhas.map(l => l.map(esc).join(';')).join('\r\n');
  await salvarECompartilhar(new TextEncoder().encode(txt), nomeArquivo(nome.endsWith('.csv') ? nome : nome + '.csv'), 'text/csv');
}

// ─── Valor por extenso (reais), para recibos ───
const UN = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove', 'dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const DZ = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const CT = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
function ate999(n: number): string {
  if (n === 100) return 'cem';
  const c = Math.floor(n / 100), r = n % 100, d = Math.floor(r / 10), u = r % 10;
  const partes: string[] = [];
  if (c) partes.push(CT[c]);
  if (r) partes.push(r < 20 ? UN[r] : DZ[d] + (u ? ' e ' + UN[u] : ''));
  return partes.join(' e ');
}
function inteiro(n: number): string {
  if (n === 0) return 'zero';
  const grupos: [number, string, string][] = [[1e9, 'bilhão', 'bilhões'], [1e6, 'milhão', 'milhões'], [1e3, 'mil', 'mil']];
  const partes: string[] = [];
  let resto = n;
  for (const [v, s, p] of grupos) {
    const q = Math.floor(resto / v);
    if (q) { partes.push(v === 1e3 && q === 1 ? 'mil' : `${ate999(q)} ${q === 1 ? s : p}`); resto %= v; }
  }
  if (resto) partes.push(ate999(resto));
  if (partes.length > 1 && resto && (resto < 100 || resto % 100 === 0)) return partes.slice(0, -1).join(' ') + ' e ' + partes[partes.length - 1];
  return partes.join(' ');
}
export function reaisPorExtenso(v: number) {
  const tot = Math.round(Math.abs(v) * 100);
  const r = Math.floor(tot / 100), c = tot % 100;
  const pr = r ? `${inteiro(r)}${r >= 1e6 && r % 1e6 === 0 ? ' de' : ''} ${r === 1 ? 'real' : 'reais'}` : '';
  const pc = c ? `${inteiro(c)} ${c === 1 ? 'centavo' : 'centavos'}` : '';
  return [pr, pc].filter(Boolean).join(' e ') || 'zero reais';
}
